/* eslint-disable @angular-eslint/component-selector */
import { Component, Input, ElementRef, KeyValueDiffers, KeyValueDiffer, ChangeDetectorRef, DoCheck, ViewContainerRef, ComponentRef, OnDestroy, Type, inject, input, output, viewChild } from '@angular/core';

import { NgStyle } from '@angular/common';
import { columnsByPin, columnGroupWidths, columnsByPinArr } from '../../utils/column';
import { TableColumn } from '../../types/table-column.type';
import { Keys } from '../../utils/keys';
import { ScrollbarHelper } from '../../services/scrollbar-helper.service';
import { translateXY } from '../../utils/translate';
import { TreeStatus, DataTableBodyCellComponent } from './body-cell.component';

interface ColumnGroupWidths {
  left: number;
  center: number;
  right: number;
  total: number;
}

interface ColumnPinGroup {
  type: string;
  columns: Array<TableColumn>;
}

interface RowNotification {
  componentType: Type<unknown>;
  notificationStatus?: unknown;
}

interface RowWithNotification extends Record<string, unknown> {
  rowNotification?: RowNotification;
}

@Component({
  selector: 'datatable-body-row',
  template: `
      @for (colGroup of _columnsByPin; track trackByGroups(i, colGroup); let i = $index) {
        <div class="datatable-row-{{ colGroup.type }} datatable-row-group"
             [ngStyle]="_groupStyles[colGroup.type]">
          @for (column of colGroup.columns; track columnTrackingFn(ii, column); let ii = $index) {
            <datatable-body-cell tabindex="-1"
                                 [attr.role]="column?.rowheader ? 'rowheader' : 'cell'"
                                 [row]="row()"
                                 [group]="group()"
                                 [expanded]="expanded()"
                                 [isSelected]="isSelected()"
                                 [rowIndex]="rowIndex()"
                                 [column]="column"
                                 [rowHeight]="rowHeight()"
                                 [displayCheck]="displayCheck()"
                                 [treeStatus]="treeStatus()"
                                 (activate)="onActivate($event, ii)"
                                 (treeAction)="onTreeAction()" />
          }
        </div>
      }
      <ng-template #rowNotificationTemplate let-notificationStatus="notificationStatus" />
    `,
  imports: [NgStyle, DataTableBodyCellComponent],
  host: {
    '[style.height.px]': 'rowHeight()',
    '(keydown)': 'onKeyDown($event)',
    '(mouseenter)': 'onMouseenter($event)',
    '[class]': 'cssClass',
    '[style.width.px]': '_columnGroupWidths.total'
  }
})
export class DataTableBodyRowComponent implements DoCheck, OnDestroy {
  private readonly scrollbarHelper = inject(ScrollbarHelper, { skipSelf: true });
  private readonly cd = inject(ChangeDetectorRef);

  // TODO: Skipped for migration because:
  //  Accessor inputs cannot be migrated as they are too complex.
  @Input() set columns(val: Array<TableColumn>) {
    this._columns = val;
    this.recalculateColumns(val);
    this.buildStylesByGroup();
  }

  get columns(): Array<TableColumn> {
    return this._columns;
  }

  // TODO: Skipped for migration because:
  //  Accessor inputs cannot be migrated as they are too complex.
  @Input() set innerWidth(val: number) {
    if (this._columns) {
      const colByPin = columnsByPin(this._columns);
      this._columnGroupWidths = columnGroupWidths(colByPin, this._columns);
    }

    this._innerWidth = val;
    this.recalculateColumns();
    this.buildStylesByGroup();
  }

  get innerWidth(): number {
    return this._innerWidth;
  }

  readonly expanded = input<boolean>(undefined);
  readonly rowClass = input<(row: unknown) => string | Record<string, boolean>>(undefined);
  readonly row = input<unknown>(undefined);
  readonly group = input<unknown>(undefined);
  readonly isSelected = input<boolean>(undefined);
  readonly rowIndex = input<number>(undefined);
  readonly displayCheck = input<(row: unknown, column?: TableColumn, value?: unknown) => boolean>(undefined);
  readonly treeStatus = input<TreeStatus>('collapsed');

  // TODO: Skipped for migration because:
  //  Accessor inputs cannot be migrated as they are too complex.
  @Input()
  set offsetX(val: number) {
    this._offsetX = val;
    this.buildStylesByGroup();
  }

  get offsetX(): number {
    return this._offsetX;
  }

  get cssClass(): string {
    let cls = 'datatable-body-row';
    if (this.isSelected()) {
      cls += ' active';
    }
    if (this.rowIndex() % 2 !== 0) {
      cls += ' datatable-row-odd';
    }
    if (this.rowIndex() % 2 === 0) {
      cls += ' datatable-row-even';
    }
    const row = this.row() as RowWithNotification;
    if (row && row.rowNotification) {
      cls += ' datatable-body-row-with-notification';
    }

    const rowClass = this.rowClass();
    if (rowClass) {
      const res = rowClass(row);
      if (typeof res === 'string') {
        cls += ` ${res}`;
      } else if (typeof res === 'object') {
        const keys = Object.keys(res);
        for (const k of keys) {
          if (res[k] === true) {
            cls += ` ${k}`;
          }
        }
      }
    }

    return cls;
  }

  readonly rowHeight = input<number | 'auto'>(undefined);

  readonly activate = output<unknown>();
  readonly treeAction = output<unknown>();

  _element: HTMLElement;
  _columnGroupWidths: ColumnGroupWidths;
  _columnsByPin: Array<ColumnPinGroup>;
  _offsetX: number;
  _columns: Array<TableColumn>;
  _innerWidth: number;
  _groupStyles: Record<string, object> = {
    left: {},
    center: {},
    right: {}
  };

  readonly viewContainerRef = viewChild('rowNotificationTemplate', { read: ViewContainerRef });

  private componentRef: ComponentRef<Component>;

  private _rowDiffer: KeyValueDiffer<object, object>;

  constructor() {
    const element = inject(ElementRef);

    this._element = element.nativeElement;
    this._rowDiffer = inject(KeyValueDiffers).find({}).create();
  }

  ngDoCheck(): void {
    const row = this.row();
    if (this._rowDiffer.diff(row as Record<string, object>)) {
      this.cd.markForCheck();
    }

    this.syncRowNotification(row as RowWithNotification);
  }

  private syncRowNotification(row: RowWithNotification): void {
    const viewContainerRef = this.viewContainerRef();
    if (viewContainerRef === undefined) {
      return;
    }

    const rowNotification = row?.rowNotification;
    if (rowNotification === undefined) {
      this.destroyRowNotificationComponent(viewContainerRef);
      return;
    }

    if (this.shouldRecreateRowNotification(rowNotification.componentType)) {
      this.createRowNotificationComponent(viewContainerRef, rowNotification.componentType);
    }

    this.updateRowNotificationInput(rowNotification.notificationStatus);
  }

  private shouldRecreateRowNotification(componentType: Type<unknown>): boolean {
    const currentComponentType = this.componentRef?.instance?.constructor as Type<unknown> | undefined;
    return this.componentRef === undefined || currentComponentType !== componentType;
  }

  private createRowNotificationComponent(viewContainerRef: ViewContainerRef, componentType: Type<unknown>): void {
    this.destroyRowNotificationComponent(viewContainerRef);
    this.componentRef = viewContainerRef.createComponent(componentType);
    viewContainerRef.insert(this.componentRef.hostView);
  }

  private updateRowNotificationInput(notificationStatus: unknown): void {
    this.componentRef?.setInput('notificationStatus', notificationStatus);
  }

  private destroyRowNotificationComponent(viewContainerRef: ViewContainerRef): void {
    if (this.componentRef === undefined) {
      return;
    }

    this.componentRef.destroy();
    this.componentRef = undefined;
    viewContainerRef.clear();
  }

  ngOnDestroy(): void {
    this.componentRef?.destroy();
  }

  trackByGroups(_index: number, colGroup: ColumnPinGroup): string {
    return colGroup.type;
  }

  columnTrackingFn(_index: number, column: TableColumn): string {
    return column.$$id;
  }

  buildStylesByGroup(): void {
    this._groupStyles.left = this.calcStylesByGroup('left');
    this._groupStyles.center = this.calcStylesByGroup('center');
    this._groupStyles.right = this.calcStylesByGroup('right');
    this.cd.markForCheck();
  }

  calcStylesByGroup(group: string): Record<string, string> {
    const widths = this._columnGroupWidths;
    const offsetX = this.offsetX;

    const styles = {
      width: `${(this._columnGroupWidths as unknown as Record<string, number>)[group]}px`
    };

    if (group === 'left') {
      translateXY(styles, offsetX, 0);
    } else if (group === 'right') {
      const bodyWidth = parseInt(this.innerWidth + '', 0);
      const totalDiff = widths.total - bodyWidth;
      const offsetDiff = totalDiff - offsetX;
      const offset = (offsetDiff + this.scrollbarHelper.width) * -1;
      translateXY(styles, offset, 0);
    }

    return styles;
  }

  onActivate(event: Record<string, unknown>, index: number): void {
    event['cellIndex'] = index;
    event['rowElement'] = this._element;
    this.activate.emit(event);
  }

  onKeyDown(event: KeyboardEvent): void {
    const keyCode = event.keyCode;
    const isTargetRow = event.target === this._element;

    const isAction =
      keyCode === Keys.return ||
      keyCode === Keys.down ||
      keyCode === Keys.up ||
      keyCode === Keys.left ||
      keyCode === Keys.right;

    if (isAction && isTargetRow) {
      event.preventDefault();
      event.stopPropagation();

      this.activate.emit({
        type: 'keydown',
        event,
        row: this.row(),
        rowElement: this._element
      });
    }
  }

  onMouseenter(event: unknown): void {
    this.activate.emit({
      type: 'mouseenter',
      event,
      row: this.row(),
      rowElement: this._element
    });
  }

  recalculateColumns(val: Array<TableColumn> = this.columns): void {
    this._columns = val;
    const colsByPin = columnsByPin(this._columns);
    this._columnsByPin = columnsByPinArr(this._columns);
    this._columnGroupWidths = columnGroupWidths(colsByPin, this._columns);
  }

  onTreeAction(): void {
    this.treeAction.emit(undefined);
  }
}
